import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';

export default function PaymentComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ── Guard: block direct URL access ──────────────────────────────────────────
  // Allow through only if arriving via:
  //   (a) React Router state  → PayPal / internal flow
  //   (b) ?session_id= param  → Stripe redirect back from Stripe's servers
	//   (c) Editor preview iframe → skip the guard so the page is editable																				 
  const stateData = location.state as {
    subscriptionID?: string;
    planName?: string;
    planId?: string;
    term?: string;
    method?: string;
    orderID?: string;
    payerName?: string;
    payerEmail?: string;
    amount?: string;
    currency?: string;
    amountTotal?: number;
    amountTax?: number;
  } | null;

  const sessionId = searchParams.get('session_id') ?? '';
	
  // Detect Readdy editor preview (page loaded in iframe) — skip redirect guards
  const isEditorPreview = typeof window !== 'undefined' && window.self !== window.top;

  const hasValidEntry = Boolean(stateData) || Boolean(sessionId) || isEditorPreview;

  useEffect(() => {
    if (!hasValidEntry) {
      navigate('/pricing', { replace: true });
    }
  }, [hasValidEntry, navigate]);

  // ── Prefer router state (PayPal path) over URL params (Stripe redirect path) ──
  const method      = stateData?.method      ?? searchParams.get('method')   ?? 'paypal';
  const planName    = stateData?.planName     ?? searchParams.get('planName') ?? '';
  const planId      = stateData?.planId       ?? searchParams.get('planId')   ?? '1';
  const term        = stateData?.term         ?? searchParams.get('term')     ?? 'M';
  const orderID     = stateData?.orderID      ?? stateData?.subscriptionID    ?? searchParams.get('orderID') ?? '';
  const payerName   = stateData?.payerName    ?? searchParams.get('payerName')  ?? '';
  const payerEmail  = stateData?.payerEmail   ?? searchParams.get('payerEmail') ?? '';
  const amount      = stateData?.amount       ?? searchParams.get('amount')     ?? '';
  const currency    = stateData?.currency     ?? searchParams.get('currency')   ?? 'USD';

  // Stripe session data fetched from server
  const [stripeDetails, setStripeDetails] = useState<{
    customerEmail: string;
    customerName: string;
    amountTotal: number;
    amountTax: number;
    currency: string;
    paymentIntentId: string;
		source: string;							 
  } | null>(null);
  const [stripeFetching, setStripeFetching] = useState(false);

  // User IP address — detected on mount
  const [userIp, setUserIp] = useState<string>('');

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((data) => setUserIp(data.ip || ''))
      .catch(() => { /* silently ignore */ });
  }, []);

  const isStripe = method === 'stripe';

  // ── Payment amounts for registration POST ──────────────────────────────────
  // For PayPal these come via router state from the payment page.
  // For Stripe they come from the session fetch (amounts are in cents → divide by 100).
  const stateAmountTotal = stateData?.amountTotal;
  const stateAmountTax   = stateData?.amountTax;

  const regAmountTotal: string = isStripe
    ? stripeDetails ? (stripeDetails.amountTotal / 100).toFixed(2) : '0.00'
    : stateAmountTotal != null ? stateAmountTotal.toFixed(2) : (amount || '0.00');

  const regAmountTax: string = isStripe
    ? stripeDetails ? (stripeDetails.amountTax / 100).toFixed(2) : '0.00'
    : stateAmountTax != null ? stateAmountTax.toFixed(2) : '0.00';

  const paidWith: string = method === 'paypal' ? 'PayPal' : 'CreditCard';

  // Derived display values
  const displayOrderId = isStripe ? stripeDetails?.paymentIntentId || sessionId : orderID;
  const displayEmail = isStripe ? stripeDetails?.customerEmail || '' : payerEmail;
  const displayName = isStripe ? stripeDetails?.customerName || '' : payerName;
  const displayAmount = isStripe
    ? stripeDetails ? (stripeDetails.amountTotal / 100).toFixed(2) : ''
    : amount;
  const displayCurrency = isStripe ? (stripeDetails?.currency?.toUpperCase() || 'USD') : currency;

  const hasPaymentDetails = Boolean(displayOrderId || displayEmail || displayAmount);

  // Hardcoded site identifier so register.asp redirects back to QBOVerifier.
  // (The QBOVerifier site hardcodes its own value in its own codebase.)
  const siteName = 'QBOVerifier';



  // ── Silent POST to xactid.com registration (hides plan/term from URL bar) ──
  const handleCompleteRegistration = () => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://id.xactid.com/biomailenroll/web/issue-token.asp';

    const fields: Record<string, string> = {
      plan: planId,
      term,
      site: siteName,
      ipaddress:   userIp,
      amountTotal: regAmountTotal,
      amountTax:   regAmountTax,
      paidWith,
    };
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const termLabel = term === 'M' ? 'Monthly' : term === 'Y' ? 'Annual' : term;
  const methodLabel = isStripe ? 'Credit Card (Stripe)' : method === 'paypal' ? 'PayPal' : method;

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const receiptEmailSentRef = useRef(false);

  // Fetch Stripe session details from Express server
  useEffect(() => {
    if (isStripe && sessionId) {
      setStripeFetching(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      fetch(`${apiUrl}/session/${sessionId}`)
        .then((r) => r.json())
        .then((session) => {
          const details = {
            customerEmail: session.customer_details?.email || '',
            customerName: session.customer_details?.name || '',
            amountTotal: session.amount_total || 0,
            amountTax: session.total_details?.amount_tax || 0,
            currency: session.currency || 'usd',
            paymentIntentId: typeof session.payment_intent === 'string'
              ? session.payment_intent
              : session.payment_intent?.id || sessionId,
							source: session.metadata?.source || '',
          };
          setStripeDetails(details);

          // Send receipt email once after we have the full details
          if (!receiptEmailSentRef.current && details.customerEmail) {
            receiptEmailSentRef.current = true;
            const today = new Date();
            const dateStr = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            const receiptApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
            fetch(`${receiptApiUrl}/send-receipt`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: details.customerEmail,
                name: details.customerName,
                orderId: details.paymentIntentId,
                planName,
                term,
                amount: (details.amountTotal / 100).toFixed(2),
                currency: details.currency.toUpperCase(),
                method: 'stripe',
                date: dateStr,
                planId,
                site: siteName,
              }),
            }).catch(() => {
              // Silently ignore — receipt is best-effort
            });
          }
        })
        .catch(() => {
          // If server unreachable just show what we have from URL params
        })
        .finally(() => setStripeFetching(false));
    }
  }, [isStripe, sessionId]);

  // For PayPal payments — send receipt once if email is available from URL params
  useEffect(() => {
    if (!isStripe && payerEmail && !receiptEmailSentRef.current) {
      receiptEmailSentRef.current = true;
      const today = new Date();
      const dateStr = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      fetch(`${apiUrl}/send-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: payerEmail,
          name: payerName,
          orderId: orderID,
          planName,
          term,
          amount,
          currency,
          method: 'paypal',
          date: dateStr,
          planId,
          site: siteName,
        }),
      }).catch(() => {
        // Silently ignore — receipt is best-effort
      });
    }
  }, [isStripe, payerEmail]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-teal-50">
      <SEO title="Payment Successful – QBOVerifier" description="Your QBOVerifier payment was successful. Complete registration to activate your account." noIndex />
      <Header />

      <div className="py-20 px-6">
        <div className="max-w-3xl mx-auto">

          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-6 animate-bounce">
              <i className="ri-checkbox-circle-line text-7xl text-white"></i>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Payment Successful!
            </h1>

            <p className="text-xl text-gray-600">
              {planName ? `Your ${planName} Plan subscription is now active.` : 'Your subscription is now active.'}
            </p>
          </div>

          {/* Receipt Section */}
          {(hasPaymentDetails || isStripe) && (
            <div className="bg-white rounded-2xl border-2 border-green-200 p-8 mb-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-receipt-line text-xl text-green-600"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Payment Receipt</h2>
                  <p className="text-sm text-gray-500">Keep this for your records</p>
                </div>
                <div className="ml-auto">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    <i className="ri-check-line mr-1"></i>Confirmed
                  </span>
                </div>
              </div>

              {stripeFetching ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-3"></div>
                  <p className="text-gray-500 text-sm">Loading receipt details...</p>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  {displayOrderId && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-50">
                      <span className="text-gray-500 font-medium">Transaction ID</span>
                      <span className="text-gray-800 font-mono text-xs bg-gray-50 px-2 py-1 rounded">{displayOrderId}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Date</span>
                    <span className="text-gray-800 font-semibold">{dateStr}</span>
                  </div>
                  {displayName && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-50">
                      <span className="text-gray-500 font-medium">Name</span>
                      <span className="text-gray-800 font-semibold">{displayName}</span>
                    </div>
                  )}
                  {displayEmail && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-50">
                      <span className="text-gray-500 font-medium">Email</span>
                      <span className="text-gray-800">{displayEmail}</span>
                    </div>
                  )}
                  {planName && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-50">
                      <span className="text-gray-500 font-medium">Plan</span>
                      <span className="text-gray-800 font-semibold">{planName}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Billing</span>
                    <span className="text-gray-800 font-semibold">{termLabel}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Payment Method</span>
                    <span className="text-gray-800 font-semibold">{methodLabel}</span>
                  </div>
                  {displayAmount && (
                    <div className="flex justify-between items-center pt-3 mt-1">
                      <span className="text-gray-900 font-bold text-base">Amount Charged</span>
                      <span className="text-2xl font-bold" style={{ color: '#29B3E6' }}>
                        ${displayAmount} <span className="text-sm text-gray-500 font-normal">{displayCurrency}</span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info Cards */}
          <div className="bg-white rounded-2xl border-2 border-green-100 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6 pb-6 border-b border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-mail-send-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Confirmation Email Sent</h3>
                <p className="text-gray-600">
                  We&apos;ve sent a confirmation email with your receipt. Please check your inbox.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 mb-6 pb-6 border-b border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                <i className="ri-user-add-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Account Activation</h3>
                <p className="text-gray-600">
                  Your account has been set up. We now need some details that are required for the processing and sending of the bioEncrypted QBOVerifier activated invoice. Please continue with the registration process.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full flex-shrink-0">
                <i className="ri-customer-service-2-line text-2xl text-purple-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Need Help?</h3>
                <p className="text-gray-600">
                  Our support team is here to help you get started. Feel free to reach out if you have any questions.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-8 mb-8 border-2 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              What&apos;s Next?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Complete Registration</h3>
                <p className="text-sm text-gray-600 text-left">Continue with the Registration so we can complete the setup of your account with your Company details. You will be asked to confirm your mobile number and email address.</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Install QBOVerifier</h3>
                <p className="text-sm text-gray-600 text-left">Install the QBOVerifier browser extension that automatically generates your new QBOVerifier invoice emails. You will be provided with instructions.</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Start Verifying</h3>
                <p className="text-sm text-gray-600 text-left">Begin using QBOVerifier today and protect your business from invoice fraud.</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <form
              method="POST"
              action="https://id.xactid.com/biomailenroll/web/issue-token.asp"
              className="contents"
            >
              <input type="hidden" name="method" value="free" />
              <input type="hidden" name="site" value="qboVerifier" />
              <button
                type="submit"
                className="px-8 py-4 text-white rounded-lg font-semibold text-lg transition-all hover:shadow-xl whitespace-nowrap text-center cursor-pointer"
                style={{ backgroundColor: '#29B3E6' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#29B3E6'}
              >
                <i className="ri-login-box-line mr-2"></i>
                Complete Registration
              </button>
            </form>

            <a
              href="/"
              className="px-8 py-4 bg-white text-gray-700 rounded-lg font-semibold text-lg border-2 border-gray-300 hover:border-gray-400 hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-center"
            >
              <i className="ri-home-line mr-2"></i>
              Return to Home
            </a>
          </div>

          {/* Support */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-600 mb-4">Questions about your purchase?</p>
            <button
              onClick={() => navigate('/contact')}
              className="text-lg font-semibold hover:underline cursor-pointer whitespace-nowrap"
              style={{ color: '#29B3E6' }}
            >
              Contact Our Support Team
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}

